import { registerGlobalValue, cache } from '../utils/registerGlobalValue'
import { DeviceInfo } from '../typings/DeviceInfo'
import { JSBridgeReturnType } from '../typings/JSBridgeProtocol'
import { BaseController } from './BaseController'
import { getSafelyWindow, type HybridBridgeLJ } from '../utils/safelyWindow';
import { StaticData } from '../typings/StaticData'
const HYBRID_BRIDGE_NAME = 'HybridBridgeLJ'

/**
 * Android WebView 控件。
 */
export class WebViewController extends BaseController {
  hybridBridge?: HybridBridgeLJ
  deviceInfo?: DeviceInfo
  staticData?: StaticData

  constructor() {
    super()
    Promise.resolve().then(async () => {
      try {
        await this.checkBridgeReady()
      } catch (error) {
        console.warn(error)
      }
    })
  }

  /**
   * 检查 HybridBridgeLJ 是否ready
   * @param timeout 超时时间
   * @returns 
   */
  private checkBridgeReady(timeout = 6000) {
    if (this.hybridBridge) return true

    const start = performance.now()

    return new Promise<boolean>((resolve, reject) => {
      const check = () => {
        if (this.hybridBridge) return resolve(true)

        const $window = getSafelyWindow()

        if ($window?.HybridBridgeLJ) {
          const hybridBridge = $window[HYBRID_BRIDGE_NAME]

          hybridBridge?.getStaticData(
            registerGlobalValue('HYBRID_BRIDGE_NAME', (data: any) => {
              this.staticData = JSON.parse(data) as StaticData
              this.deviceInfo = this.staticData
              this.hybridBridge = hybridBridge

              return resolve(true)
            }),
          )
        }
        if (performance.now() - start > (timeout || 6000)) {
          // console.warn('HybridBridge 初始化超时~')
          return reject(new Error('HybridBridge 初始化超时~'))
        }

        // 逐帧监测
        requestAnimationFrame(check)
      }

      // 开启监测
      check()
    })
  }

  async checkReady(timeout = 6000) {
    const res: JSBridgeReturnType<boolean> = [null, null]

    if (this.hybridBridge) {
      res[0] = true
      return res
    }

    try {
      res[0] = await this.checkBridgeReady(timeout)
    } catch (error) {
      res[0] = false
      res[1] = error as Error
    }

    return res
  }

  async getStaticData(timeout = 6000): Promise<JSBridgeReturnType<StaticData | false>> {
    const [ready, readyError] = await this.checkReady(timeout)
    if (!ready) return [false, readyError]
    return [this.staticData || false, null]
  }

  get ready() {
    return !!this.hybridBridge
  }

  get scheme() {
    return this.deviceInfo?.scheme || ''
  }

  createSchemeLink(schemeUrl: string) {
    if (schemeUrl.startsWith(this.scheme)) {
      return schemeUrl
    }
    return this.scheme + '://' + schemeUrl
  }

  async callAndBackfeed<T>(scheme: string) {
    const res = [false, null] as JSBridgeReturnType<T | false>
    const [ready, readyError] = await this.checkReady()

    if (!ready) {
      res[0] = false
      res[1] = readyError

      return res
    }

    return await new Promise<JSBridgeReturnType<T | false>>(async (resolve) => {
      const actionUrl = this.createSchemeLink(scheme)

      const msg = JSON.stringify({
        actionUrl,
        functionName: registerGlobalValue(this.createSchemeLink(scheme), (data: string) => {
          const d: any = (() => {
            try {
              return JSON.parse(data)
            } catch (e){
              return data
            }
          })() 

          res[0] = (() => {
            if (d === 1) return true
            if (d === 0) return false
            return d
          })() as T

          resolve(res)
        }),
      })

      this.hybridBridge!.callAndBackfeed(msg)
    })
  }

  async callAndListen<T>(scheme: string, callback: (data: T) => void) {
    const res = [false, null] as JSBridgeReturnType<false | (() => void)>
    const [ready, readyError] = await this.checkReady()

    if (!ready) {
      res[0] = false
      res[1] = readyError

      return res
    }

    return await new Promise<JSBridgeReturnType<false | (() => void)>>(async (resolve) => {
      const actionUrl = this.createSchemeLink(scheme)
      const _callback = (data: string) => {
        const d = (() => {
          try {
            return JSON.parse(data)
          } catch (e) {
            return data
          }
        })() 

        const callbackParam = (() => {
          if (d === 1) return true
          if (d === 0) return false
          return d
        })() as T

        callback(callbackParam)
        res[0] = () => {
          // 清理掉当前的回调引用
          const callbacks = cache[actionUrl]
          if (!callbacks) return

          const idx = callbacks.queue.findIndex((func) => func.callback === _callback)
          if (idx !== -1) {
            callbacks.queue.splice(idx, 1)
          }
        }
        resolve(res)
      }

      const msg = JSON.stringify({
        actionUrl,
        functionName: registerGlobalValue(actionUrl, _callback, false),
      })

      this.hybridBridge!.callAndListen(msg)
    })
  }
}
